import React from 'react';
import { useAgents } from '../../hooks/useAgents';
import { propertyTypes } from '../../constants/propertyTypes';
import { Button } from '../ui/Button';
import { X } from 'lucide-react';

interface StatisticsFiltersProps {
  dateRange: [Date, Date];
  onDateRangeChange: (range: [Date, Date]) => void;
  selectedAgents: string[];
  onAgentsChange: (agents: string[]) => void;
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
}

export const StatisticsFilters: React.FC<StatisticsFiltersProps> = ({
  dateRange,
  onDateRangeChange,
  selectedAgents,
  onAgentsChange,
  selectedTypes,
  onTypesChange,
}) => {
  const { agents } = useAgents();

  const handleReset = () => {
    onDateRangeChange([new Date(new Date().getFullYear(), 0, 1), new Date()]);
    onAgentsChange([]);
    onTypesChange([]);
  };

  return (
    <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          Filtres
        </h2>
        <Button
          variant="ghost"
          onClick={handleReset}
          className="flex items-center gap-2 text-sm text-gray-500"
        >
          <X className="h-4 w-4" />
          Réinitialiser
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Période
          </label>
          <div className="mt-2 grid gap-4">
            <input
              type="date"
              value={dateRange[0].toISOString().split('T')[0]}
              onChange={(e) => onDateRangeChange([new Date(e.target.value), dateRange[1]])}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
            <input
              type="date"
              value={dateRange[1].toISOString().split('T')[0]}
              onChange={(e) => onDateRangeChange([dateRange[0], new Date(e.target.value)])}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Commerciaux
          </label>
          <div className="mt-2 space-y-2">
            {agents.map((agent) => (
              <label key={agent.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedAgents.includes(agent.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onAgentsChange([...selectedAgents, agent.id]);
                    } else {
                      onAgentsChange(selectedAgents.filter(id => id !== agent.id));
                    }
                  }}
                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {agent.name}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Types de biens
          </label>
          <div className="mt-2 space-y-2">
            {propertyTypes.map((type) => (
              <label key={type} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(type)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onTypesChange([...selectedTypes, type]);
                    } else {
                      onTypesChange(selectedTypes.filter(t => t !== type));
                    }
                  }}
                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {type}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};